import { generateKeyBetween, generateNKeysBetween } from 'fractional-indexing';
import { compareRepositoryEntryRank } from './util';

export { generateKeyBetween, generateNKeysBetween };

/** Append after the current maximum key (e.g. archive / unarchive stacks). */
export function rankKeyAppendAfter(maxExisting: string | null): string {
	return generateKeyBetween(maxExisting, null);
}

/** Full table reorder: same as replacing dense integer ranks 0..n-1. */
export function lexRankKeysForFullReorder(length: number): string[] {
	if (length <= 0) {
		return [];
	}
	return generateNKeysBetween(null, null, length);
}

/**
 * After a permutation, reassign ranks with minimal writes: walk the new order left-to-right,
 * and only update a row when its current key is not strictly between the left neighbor’s
 * (possibly just reassigned) key and the right neighbor’s **original** key.
 *
 * Returns `null` if we cannot use fractional keys (e.g. legacy numeric ranks) — caller should
 * fall back to {@link lexRankKeysForFullReorder}.
 */
export function minimalRepositoryEntryRankUpdatesForNewOrder(
	newOrderIds: string[],
	snapshotRankById: Map<string, string | number | undefined | null>,
): { id: string; rank: string }[] | null {
	const snap = new Map<string, string>();
	for (const id of newOrderIds) {
		const r = snapshotRankById.get(id);
		if (typeof r === 'number') {
			return null;
		}
		if (r == null || r === '') {
			return null;
		}
		snap.set(id, String(r));
	}
	const mutable = new Map(snap);
	const updates: { id: string; rank: string }[] = [];
	for (let i = 0; i < newOrderIds.length; i++) {
		const id = newOrderIds[i];
		const prevId = i > 0 ? newOrderIds[i - 1] : null;
		const nextId = i < newOrderIds.length - 1 ? newOrderIds[i + 1] : null;
		const prevKey = prevId == null ? null : mutable.get(prevId) ?? null;
		const nextKey = nextId == null ? null : snap.get(nextId) ?? null;
		const cur = mutable.get(id);
		if (cur == null) {
			return null;
		}
		const ok =
			(prevKey === null || prevKey < cur) && (nextKey === null || cur < nextKey);
		if (!ok) {
			try {
				const newR = generateKeyBetween(prevKey, nextKey);
				mutable.set(id, newR);
				updates.push({ id, rank: newR });
			} catch {
				return null;
			}
		}
	}
	return updates;
}

/**
 * Rank patches for a new visual order: minimal fractional updates when possible, else full regen.
 */
export function repositoryEntryRankUpdatesForReorder(
	newOrderIds: string[],
	snapshotRankById: Map<string, string | number | undefined | null>,
): { id: string; rank: string }[] {
	if (newOrderIds.length === 0) {
		return [];
	}
	const minimal = minimalRepositoryEntryRankUpdatesForNewOrder(
		newOrderIds,
		snapshotRankById,
	);
	if (minimal !== null) {
		return minimal;
	}
	const keys = lexRankKeysForFullReorder(newOrderIds.length);
	return newOrderIds.map((id, i) => ({ id, rank: keys[i] }));
}

/**
 * Rank for a new repository entry at the top of the list (ascending display order).
 * With fractional keys, prepends before the current minimum. Legacy numeric ranks offline cannot be
 * used as fractional bounds — we assign a fresh key without rewriting existing rows (sort may look
 * odd until a reorder or migration).
 */
export function nextRepositoryEntryPrependRank(
	existingSameRepository: { id: string; rank?: number | string }[],
): { rank: string } {
	const sorted = [...existingSameRepository].sort(compareRepositoryEntryRank);
	if (sorted.length === 0) {
		return { rank: generateKeyBetween(null, null) };
	}
	if (sorted.some((e) => typeof e.rank === 'number')) {
		return { rank: generateKeyBetween(null, null) };
	}
	const minKey = String(sorted[0].rank ?? '');
	return { rank: generateKeyBetween(null, minKey) };
}

/** Sort repository-entry-like rows by rank (delegates to shared comparator). */
export function sortRepositoryEntriesByRank<T extends { rank?: number | string }>(
	rows: T[],
): T[] {
	return [...rows].sort(compareRepositoryEntryRank);
}
